import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Divider,
  Chip,
  Avatar,
  CircularProgress,
  Alert,
  Tooltip,
  Button,
  Autocomplete,
  TextField,
  Collapse,
} from '@mui/material';
import { Close, AccountTree, OpenInNew, Delete, Add, TableChart, ExpandMore, ExpandLess } from '@mui/icons-material';
import { extractErrorMessage } from '../../../utils/errorUtils';
import areaService from '../services/areaService';
import correspondenceTypeService from '../../correspondence-types/services/correspondenceTypeService';
import retentionService from '../../retentions/services/retentionService';
import axiosInstance from '../../../api/axiosConfig';

const AreaDetailModal = ({ open, onClose, areaId }) => {
  const navigate = useNavigate();
  const [area, setArea] = useState(null);
  const [correspondenceTypes, setCorrespondenceTypes] = useState([]);
  const [retentions, setRetentions] = useState([]);
  const [companyUsers, setCompanyUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [ctOpen, setCtOpen] = useState(false);

  useEffect(() => {
    if (open && areaId) {
      loadData();
    }
  }, [open, areaId]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [areaRes, ctData, retentionData] = await Promise.all([
        areaService.getById(areaId),
        correspondenceTypeService.getAll({ areaId, limit: 100 }),
        retentionService.getAll({ areaId, limit: 100 }),
      ]);
      const areaData = areaRes.data || areaRes;
      setArea(areaData);
      setCorrespondenceTypes(ctData.data || []);
      setRetentions(retentionData.data || []);

      const cId = areaData?.companyId || areaData?.company?.id;
      if (cId) {
        const usersRes = await axiosInstance.get('/users', {
          params: { companyId: String(cId), limit: 200 },
        }).catch(() => ({ data: { data: [] } }));
        setCompanyUsers(usersRes.data?.data || []);
      }
    } catch (err) {
      setError('Error al cargar los datos del area');
    } finally {
      setLoading(false);
    }
  };

  const handleAddUsers = async () => {
    if (selectedUsers.length === 0) return;
    setSubmitting(true);
    try {
      await areaService.assignUsers(areaId, selectedUsers.map(u => parseInt(u.id)));
      setSelectedUsers([]);
      await loadData();
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemoveUser = async (userId) => {
    setSubmitting(true);
    try {
      await areaService.removeUser(areaId, userId);
      await loadData();
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const users = (area?.areaUsers?.map((au) => au.user) || [])
    .slice()
    .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  const assignedUserIds = new Set(users.map((u) => String(u.id)));
  const availableUsers = companyUsers
    .filter((u) => !assignedUserIds.has(String(u.id)))
    .slice()
    .sort((a, b) => (a.name || '').localeCompare(b.name || ''));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 6 }}>
        <AccountTree color="primary" />
        <Typography variant="h6" component="span">
          {area?.name || 'Detalle del Area'}
        </Typography>
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {loading && (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        )}

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {!loading && !error && area && (
          <>
            {/* Informacion general + Retenciones */}
            <Typography variant="overline" color="text.secondary" fontWeight="bold">
              Informacion General
            </Typography>
            <Box sx={{ mt: 1, mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Box>
                <Typography variant="caption" color="text.secondary">Codigo</Typography>
                <Typography variant="body2" fontWeight="medium">{area.code || '\u2014'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Empresa</Typography>
                <Typography variant="body2" fontWeight="medium">{area.company?.name || '\u2014'}</Typography>
              </Box>
              {retentions.length > 0 && (
                <Box sx={{ width: '100%' }}>
                  <Typography variant="caption" color="text.secondary">Tabla de Retencion (TRD)</Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.5 }}>
                    {retentions.slice().sort((a, b) => (a.name || '').localeCompare(b.name || '')).map((r) => (
                      <Box key={r.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TableChart sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="body2" fontWeight="medium">{r.name || r.code}</Typography>
                        {r.type && <Chip label={r.type} size="small" variant="outlined" />}
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
              {area.description && (
                <Box sx={{ width: '100%' }}>
                  <Typography variant="caption" color="text.secondary">Descripcion</Typography>
                  <Typography variant="body2">{area.description}</Typography>
                </Box>
              )}
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Tipos de correspondencia - Collapsible */}
            <Box
              onClick={() => setCtOpen(!ctOpen)}
              sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }}
            >
              <Typography variant="overline" color="text.secondary" fontWeight="bold" sx={{ flex: 1 }}>
                Tipos de Correspondencia ({correspondenceTypes.length})
              </Typography>
              <IconButton size="small">
                {ctOpen ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
              </IconButton>
            </Box>
            <Collapse in={ctOpen}>
              <Box sx={{ mt: 1, mb: 1 }}>
                {correspondenceTypes.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                    No hay tipos de correspondencia asociados
                  </Typography>
                ) : (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                    {correspondenceTypes.map((ct) => (
                      <Chip key={ct.id} label={ct.name} size="small" variant="outlined" />
                    ))}
                  </Box>
                )}
              </Box>
            </Collapse>

            <Divider sx={{ my: 2 }} />

            {/* Usuarios */}
            <Typography variant="overline" color="text.secondary" fontWeight="bold">
              Usuarios ({users.length})
            </Typography>
            <Box sx={{ mt: 1 }}>
              {users.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                  No hay usuarios asignados a esta area
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {users.map((user) => (
                    <Box key={user.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Avatar sx={{ width: 32, height: 32, fontSize: 14 }}>
                        {user.name?.[0]?.toUpperCase()}
                      </Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" fontWeight="medium" noWrap>
                          {user.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {user.email}
                        </Typography>
                      </Box>
                      <Tooltip title="Ver usuario">
                        <IconButton size="small" onClick={() => { onClose(); navigate(`/users/${user.id}`); }}>
                          <OpenInNew fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Remover del area">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleRemoveUser(user.id)}
                          disabled={submitting}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  ))}
                </Box>
              )}

              {/* Agregar usuarios - Autocomplete multi-select */}
              {availableUsers.length > 0 && (
                <Box sx={{ display: 'flex', gap: 1, mt: 2, alignItems: 'flex-start' }}>
                  <Autocomplete
                    multiple
                    size="small"
                    options={availableUsers}
                    value={selectedUsers}
                    onChange={(_, newValue) => setSelectedUsers(newValue)}
                    getOptionLabel={(option) => `${option.name} — ${option.email}`}
                    filterSelectedOptions
                    renderInput={(params) => (
                      <TextField {...params} label="Agregar Usuarios" placeholder="Buscar..." />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          {...getTagProps({ index })}
                          key={option.id}
                          label={option.name}
                          size="small"
                        />
                      ))
                    }
                    sx={{ flex: 1 }}
                  />
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<Add />}
                    onClick={handleAddUsers}
                    disabled={selectedUsers.length === 0 || submitting}
                    sx={{ mt: 0.5, whiteSpace: 'nowrap' }}
                  >
                    Agregar
                  </Button>
                </Box>
              )}
            </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AreaDetailModal;
